import {
  ChangeDetectionStrategy,
  Component,
  ComponentRef,
  DestroyRef,
  EventEmitter,
  Output,
  ViewChild,
  ViewContainerRef,
  computed,
  effect,
  inject,
  input,
  reflectComponentType,
  type Type,
} from '@angular/core';

import { WIDGET_REGISTRY, type EmbeddedReportKey } from './widget-registry';

/**
 * Minimal shape this host needs from a `VisibleWidgetTile`. We deliberately
 * keep it loose (instead of importing `VisibleWidgetTile` from
 * `my-dashboard.component.ts`) to avoid a circular import — the host lives
 * in `shared/` and is imported BY the dashboard page, not the other way
 * around.
 */
export interface WidgetHostTile {
  /** Backend `widget_key` (passed through to the inner report as the only
   *  visible widget — drives the report's `mixedMode` filter). */
  id: string;
  /** Report slug used to look up the lazy loader in `WIDGET_REGISTRY`. */
  reportKey: EmbeddedReportKey | null;
  /** Catalog page id forwarded to reports that accept `[catalogPageId]`. */
  pageId?: number | null;
  /**
   * Per-placement `custom_config` persisted on the backend. Forwarded to
   * the mounted report as `embeddedCustomConfig` so it can rehydrate any
   * UI state that the user picked on the source page before pinning
   * (e.g. SEO Overview persists the selected platform here so the pinned
   * card renders Meta or Google ads data accordingly — not just whatever
   * the report's standalone default is).
   */
  customConfig?: Record<string, any> | null;
}

/** Payload emitted by `(instanceReady)` whenever the dynamic component is
 *  (re)mounted or destroyed. `instance` is `null` when the slot is empty. */
export interface WidgetHostInstanceEvent {
  instance: object | null;
  reportKey: EmbeddedReportKey | null;
}

/**
 * Generic host that replaces the giant `@switch (tile.reportKey)` block in
 * `my-dashboard.component.html`. Looks the report up in `WIDGET_REGISTRY`,
 * lazy-loads the component class, mounts it via `ViewContainerRef`, and
 * forwards the same `[mixedMode]` / `[visibleWidgetKeys]` / `[catalogPageId]`
 * inputs the static template was passing per case.
 *
 * Because the loader is dynamic, only the report kinds with at least one
 * pinned tile are pulled into the bundle — the other report kinds stay
 * lazy.
 *
 * The `(instanceReady)` output exposes the live component instance so the
 * parent can keep doing what `@ViewChildren(SomeComponent)` used to do
 * (broadcasting date-range changes, calling `loadPersonalizedDashboardLayout()`,
 * etc.) without statically importing every report class.
 */
@Component({
  selector: 'app-widget-host',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `<ng-container #slot></ng-container>`,
})
export class WidgetHostComponent {
  readonly tile = input.required<WidgetHostTile>();
  /** Forwarded to `app-social-platform` via `[embeddedColSpan]`. Other
   *  reports don't declare this input, so we gate it on `reportKey`. */
  readonly embeddedColSpan = input<number | null>(null);

  @Output() readonly instanceReady = new EventEmitter<WidgetHostInstanceEvent>();

  @ViewChild('slot', { read: ViewContainerRef, static: true })
  private slot!: ViewContainerRef;

  private readonly destroyRef = inject(DestroyRef);

  private mountedRef: ComponentRef<unknown> | null = null;
  /** Token of the load that is currently expected to mount. Used to
   *  discard stale async resolutions if the tile changes mid-flight. */
  private currentLoadToken = 0;
  /**
   * Last input values pushed into the currently-mounted component via
   * `setInput()`. Used by {@link applyInputs} to skip rebroadcasting an
   * input whose effective value has not changed.
   *
   * Why this matters (perf): the parent `MyDashboardComponent` rebuilds
   * its `dashboards` signal on every layout refresh — adding a single
   * pinned card causes `visibleWidgets` to emit fresh tile objects for
   * EVERY existing tile (same content, new references). Without this
   * dedup, the `inputs()` computed below produces a fresh
   * `visibleWidgetKeys: [t.id]` array every refresh, `applyInputs()`
   * blindly calls `ref.setInput(...)` for every input, and that
   * triggers an Angular CD cycle inside the embedded report. The
   * report then re-evaluates its template, which in many cases
   * re-creates child component config objects (e.g. the
   * `[config]="{...}"` literal in `contracts-report.component.html`)
   * — those child components see a "config changed" `ngOnChanges` and
   * fire a full API refetch + D3 chart re-render. The visible symptom
   * was the graph cards becoming very slow whenever the user pinned
   * another card. Skipping `setInput()` for unchanged values cuts the
   * cascade off at its source.
   */
  private lastAppliedInputs: Record<string, unknown> = {};
  /**
   * The `reportKey` currently mounted (or being loaded). Tracked so
   * `mountForKey()` can no-op when called with the same key.
   *
   * Why this matters: the parent (`MyDashboardComponent`) rebuilds its
   * `dashboards` signal on every layout refresh, which causes
   * `unifiedTiles()` to recompute and emit fresh `tile` objects with
   * the SAME `reportKey` to this host. Without this guard we would
   * dispose + remount the embedded report on every parent refresh —
   * each remount re-runs the report's constructor effects and re-fires
   * its initial `GET /dashboards` call, producing N duplicate layout
   * requests per pinned tile per refresh.
   */
  private currentKey: EmbeddedReportKey | null = null;

  /** Stable view of `tile().reportKey` so the mount effect only re-runs
   *  on actual key changes (not on every `tile` reference change). */
  private readonly reportKey = computed<EmbeddedReportKey | null>(
    () => this.tile().reportKey,
  );

  private readonly inputs = computed<Record<string, unknown>>(() => {
    const t = this.tile();
    const result: Record<string, unknown> = {
      mixedMode: true,
      visibleWidgetKeys: [t.id],
      catalogPageId: t.pageId ?? null,
    };
    // Only `social-platform` declares an `embeddedColSpan` input.
    // `setInput()` would throw if we forwarded it to reports that don't
    // declare it, so gate it on the report key.
    if (t.reportKey === 'social-platform') {
      const span = this.embeddedColSpan();
      if (span != null) {
        result['embeddedColSpan'] = span;
      }
    }
    // Forward `embeddedCustomConfig` for every report — `applyInputs`
    // already gates on each component's declared inputs, so reports that
    // don't accept it are skipped silently. SEO Overview is the first
    // consumer (rehydrates the platform `view` dropdown).
    const cfg = t.customConfig;
    if (cfg != null) {
      result['embeddedCustomConfig'] = cfg;
    }
    return result;
  });

  constructor() {
    // Re-mount only when the tile's reportKey actually changes.
    // `reportKey` is a computed, so default `===` equality already
    // suppresses re-fires when the parent rewrites `tile()` with a
    // different object that carries the same key.
    effect(() => {
      const key = this.reportKey();
      void this.mountForKey(key);
    });

    // Push input changes (mixedMode/visibleWidgetKeys/catalogPageId/etc.)
    // to the already-mounted instance without remounting.
    effect(() => {
      const next = this.inputs();
      const ref = this.mountedRef;
      if (!ref) return;
      this.applyInputs(ref, next);
    });

    this.destroyRef.onDestroy(() => this.disposeMounted());
  }

  private async mountForKey(key: EmbeddedReportKey | null): Promise<void> {
    // Defensive belt-and-suspenders dedup: even though the calling effect
    // is now gated on a `reportKey` computed, this guard ensures any
    // future caller (or stale promise resolution) can't accidentally
    // tear down and re-mount the same report.
    if (key === this.currentKey && (key === null || this.mountedRef !== null)) {
      return;
    }

    const myToken = ++this.currentLoadToken;

    // Eagerly clear any current mount so the slot is empty while loading.
    this.disposeMounted();
    this.currentKey = key;

    if (!key) {
      this.emitReady(null, null);
      return;
    }

    const loader = WIDGET_REGISTRY[key];
    if (!loader) {
      this.emitReady(null, key);
      return;
    }

    let ComponentClass: Type<unknown>;
    try {
      ComponentClass = await loader();
    } catch {
      if (myToken === this.currentLoadToken) {
        // Loader failed — clear `currentKey` so a future re-fire with
        // the same key can retry instead of being no-op'd by the guard
        // above.
        this.currentKey = null;
        this.emitReady(null, key);
      }
      return;
    }

    // Tile changed while we were loading — discard.
    if (myToken !== this.currentLoadToken) return;

    const ref = this.slot.createComponent(ComponentClass);
    this.applyInputs(ref, this.inputs());
    ref.changeDetectorRef.markForCheck();
    this.mountedRef = ref;
    this.emitReady(ref.instance as object, key);
  }

  private applyInputs(
    ref: ComponentRef<unknown>,
    inputs: Record<string, unknown>,
  ): void {
    const declared = WidgetHostComponent.getDeclaredInputs(ref.componentType);
    for (const k of Object.keys(inputs)) {
      // Skip inputs the target component doesn't declare. Angular 19's
      // `setInput()` reports unknown inputs via `console.error` (NG0303,
      // not a thrown exception), so a try/catch wouldn't suppress them —
      // we have to gate the call up-front.
      //
      // Why this is needed: not every report accepts every input (e.g.
      // `monthly-report-1`/`monthly-report-2` don't declare
      // `catalogPageId`, only social-platform declares `embeddedColSpan`).
      if (!declared.has(k)) continue;
      const next = inputs[k];
      // Skip if the new value is shallow-equal to the last one applied
      // for this key. See `lastAppliedInputs` doc above for the
      // perf-cascade this guards against.
      if (WidgetHostComponent.shallowInputEqual(this.lastAppliedInputs[k], next)) {
        continue;
      }
      try {
        ref.setInput(k, next);
        this.lastAppliedInputs[k] = next;
      } catch {
        // Defensive: any other unexpected error from setInput is
        // swallowed so a single bad input can't break the whole tile.
      }
    }
  }

  /**
   * Shallow equality used by {@link applyInputs} to dedup `setInput`
   * calls. Handles the input shapes this host actually pushes:
   *  - primitives (`mixedMode`, `catalogPageId`, `embeddedColSpan`) → strict `===`.
   *  - 1D string arrays (`visibleWidgetKeys`) → element-wise compare.
   * Anything else falls back to strict `===` so we err on the side of
   * pushing the value through.
   */
  private static shallowInputEqual(a: unknown, b: unknown): boolean {
    if (a === b) return true;
    if (Array.isArray(a) && Array.isArray(b)) {
      if (a.length !== b.length) return false;
      for (let i = 0; i < a.length; i++) {
        if (a[i] !== b[i]) return false;
      }
      return true;
    }
    return false;
  }

  /**
   * Reflection cache: maps each report component class to the set of
   * `templateName`s of inputs it declares. Built lazily on first mount
   * of each report kind. Static so the cache survives host re-creation
   * (e.g. when a tile is dragged or re-pinned).
   */
  private static readonly declaredInputsCache = new WeakMap<Type<unknown>, ReadonlySet<string>>();

  private static getDeclaredInputs(componentType: Type<unknown>): ReadonlySet<string> {
    const cached = WidgetHostComponent.declaredInputsCache.get(componentType);
    if (cached) return cached;
    const meta = reflectComponentType(componentType);
    const names = new Set<string>(
      meta?.inputs.map((i) => i.templateName) ?? [],
    );
    WidgetHostComponent.declaredInputsCache.set(componentType, names);
    return names;
  }

  private disposeMounted(): void {
    if (this.mountedRef) {
      this.mountedRef.destroy();
      this.mountedRef = null;
      this.emitReady(null, null);
    }
    this.slot?.clear();
    // Reset the per-input dedup cache so the next mount (e.g. when the
    // tile's reportKey changes) starts from a clean slate — otherwise
    // the new component might miss its very first `setInput()` call
    // because a stale entry from the previously mounted component
    // happens to be shallow-equal.
    this.lastAppliedInputs = {};
    //  remount with the same key must reset `currentKey` themselves (or call via `mountForKey`, which manages the field). We deliberately do NOT clear it here so the host-destroy path doesn't trick a stale async loader resolution into re-asserting a key for a torn-down host.
  }

  private emitReady(instance: object | null, reportKey: EmbeddedReportKey | null): void {
    this.instanceReady.emit({ instance, reportKey });
  }

  getInstance(): object | null {
    return (this.mountedRef?.instance as object | null) ?? null;
  }
}
